import { useEffect, useRef, useState } from 'react'
import type { MemoAction, MemoKeyPoint, PreviousAnalysis } from '@ai-meeting/shared'
import type { RealtimeToken } from '@soniox/client'
import type { LiveMemoStatus } from '@/hooks/useProxySttRecording'
import { tokensToPlainTranscript } from '@/utils/tokensToPlainTranscript'

export type { LiveMemoStatus }

interface UseLiveMemoInsightsOptions {
  tokens: RealtimeToken[]
  memoStatus: LiveMemoStatus
  memo: PreviousAnalysis | null
  memoError: string | null
  enabled: boolean
}

interface UseLiveMemoInsightsResult {
  status: LiveMemoStatus
  memo: PreviousAnalysis | null
  sentTranscript: string
  keyPoints: MemoKeyPoint[]
  actions: MemoAction[]
  error: string | null
}

/**
 * Adapts the raw `summarize.*` push state exposed by `useProxySttRecording`
 * into the live-memo sidebar contract. The server now re-analyzes the whole
 * transcript on a fixed cadence and pushes full-replace snapshots over the
 * WebSocket, so this hook no longer owns any scheduling, HTTP calls, or
 * client-side merge logic — it only derives `sentTranscript` (a snapshot of
 * the transcript at the moment the latest `memo` arrived) and resets state
 * when `enabled` flips to false (meeting ends / meeting switch).
 *
 * @returns Live status, memo snapshot, sent transcript, and derived
 *   `keyPoints` / `actions` arrays for the insights sidebar.
 */
export function useLiveMemoInsights({
  tokens,
  memoStatus,
  memo,
  memoError,
  enabled,
}: UseLiveMemoInsightsOptions): UseLiveMemoInsightsResult {
  const lastMemoRef = useRef<PreviousAnalysis | null>(null)
  const [sentTranscript, setSentTranscript] = useState('')

  useEffect(() => {
    if (!enabled) {
      lastMemoRef.current = null
      setSentTranscript('')
      return
    }

    if (memo !== null && memo !== lastMemoRef.current) {
      lastMemoRef.current = memo
      setSentTranscript(tokensToPlainTranscript(tokens))
    }
  }, [enabled, memo, tokens])

  if (!enabled) {
    return {
      status: 'idle',
      memo: null,
      sentTranscript: '',
      keyPoints: [],
      actions: [],
      error: null,
    }
  }

  return {
    status: memoStatus,
    memo,
    sentTranscript,
    keyPoints: memo?.key_points ?? [],
    actions: memo?.actions ?? [],
    error: memoError,
  }
}
